import type { PopoverOrigin } from '@mui/material/Popover';

export type PopoverArrowPlacement =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
  | 'left-top'
  | 'left-center'
  | 'left-bottom'
  | 'right-top'
  | 'right-center'
  | 'right-bottom';

export type ElementRect = { top: number; left: number; width: number; height: number };

const ORIGIN_MAP: Record<
  PopoverArrowPlacement,
  { anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin }
> = {
  'top-left': {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  },
  'top-center': {
    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
    transformOrigin: { vertical: 'top', horizontal: 'center' },
  },
  'top-right': {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
  },
  'bottom-left': {
    anchorOrigin: { vertical: 'top', horizontal: 'left' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
  },
  'bottom-center': {
    anchorOrigin: { vertical: 'top', horizontal: 'center' },
    transformOrigin: { vertical: 'bottom', horizontal: 'center' },
  },
  'bottom-right': {
    anchorOrigin: { vertical: 'top', horizontal: 'right' },
    transformOrigin: { vertical: 'bottom', horizontal: 'right' },
  },
  'left-top': {
    anchorOrigin: { vertical: 'top', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  },
  'left-center': {
    anchorOrigin: { vertical: 'center', horizontal: 'right' },
    transformOrigin: { vertical: 'center', horizontal: 'left' },
  },
  'left-bottom': {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
  },
  'right-top': {
    anchorOrigin: { vertical: 'top', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
  },
  'right-center': {
    anchorOrigin: { vertical: 'center', horizontal: 'left' },
    transformOrigin: { vertical: 'center', horizontal: 'right' },
  },
  'right-bottom': {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'bottom', horizontal: 'right' },
  },
};

export function getPopoverOrigin(placement: PopoverArrowPlacement) {
  return ORIGIN_MAP[placement];
}

/** Centres the arrow on the anchor, clamped so it cannot leave the paper. */
export function getArrowOffset(anchorRect: ElementRect, paperRect: ElementRect, arrowSize: number) {
  const anchorCenterX = anchorRect.left - paperRect.left + anchorRect.width / 2;
  const anchorCenterY = anchorRect.top - paperRect.top + anchorRect.height / 2;

  const minOffset = arrowSize / 2;

  return {
    offsetX: Math.max(
      minOffset,
      Math.min(anchorCenterX - arrowSize / 2, paperRect.width - arrowSize * 2),
    ),
    offsetY: Math.max(
      minOffset,
      Math.min(anchorCenterY - arrowSize / 2, paperRect.height - arrowSize * 2),
    ),
  };
}

export function getPaperOffsetStyles(
  placement: PopoverArrowPlacement,
  [primary, secondary]: [number, number],
) {
  const offsetBySide: Record<string, Record<string, [number, number]>> = {
    top: { left: [-primary, secondary], center: [0, secondary], right: [primary, secondary] },
    bottom: { left: [-primary, -secondary], center: [0, -secondary], right: [primary, -secondary] },
    left: { top: [secondary, -primary], center: [secondary, 0], bottom: [secondary, primary] },
    right: { top: [-secondary, -primary], center: [-secondary, 0], bottom: [-secondary, primary] },
  };

  const [side = '', align = 'center'] = placement.split('-');
  const [translateX, translateY] = offsetBySide[side]?.[align] ?? [0, 0];

  return { translate: `${translateX}px ${translateY}px` };
}
